import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/core/AuthContext';
import { fetchFactoriesByTenantId, fetchUserFactoryRoles, type UserFactoryRoleWithDetails } from '@/core/api';
import { useTenantStore } from '@/core/hooks/useTenant';

export const useUserFactories = () => {
  const { user, profile } = useAuth();
  const { selectedTenantId } = useTenantStore();

  return useQuery({
    queryKey: ['user-factories', user?.id, selectedTenantId, profile?.is_super_admin],
    queryFn: async () => {
      if (!user || !selectedTenantId) return [];

      if (profile?.is_super_admin) {
        const factories = await fetchFactoriesByTenantId(selectedTenantId);
        return factories.map(factory => ({
          id: `super-admin-${factory.id}`,
          factory_id: factory.id,
          tenant_id: factory.tenant_id,
          role: 'supervisor' as const,
          status: 'approved' as const,
          factory: {
            id: factory.id,
            name: factory.name,
            code: factory.code,
            tenant_id: factory.tenant_id,
          },
        })) as UserFactoryRoleWithDetails[];
      }

      const roles = await fetchUserFactoryRoles(user.id);
      return roles.filter(role => role.tenant_id === selectedTenantId && role.status === 'approved');
    },
    enabled: !!user && !!selectedTenantId,
    staleTime: 60 * 1000,
  });
};
